/**
 * Authentication Guard Hook
 * Provides role-based access control and admin-specific authentication flows
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/domains/auth/context/auth-context';
import type { User } from '@/infrastructure/api';

export interface AuthGuardOptions {
  /** Required roles for access */
  requiredRoles?: string[];
  /** Required permissions for access */
  requiredPermissions?: string[];
  /** Whether admin access is required */
  requireAdmin?: boolean;
  /** Redirect path if access is denied */
  redirectTo?: string;
  /** Whether to show loading state */
  showLoading?: boolean;
}

export interface AuthGuardResult {
  /** Current authenticated user */
  user: User | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether user has required access */
  hasAccess: boolean;
  /** Whether user is an admin */
  isAdmin: boolean;
  /** Whether user is a super admin */
  isSuperAdmin: boolean;
  /** Whether check is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Check if user has specific role */
  hasRole: (role: string) => boolean;
  /** Check if user has specific permission */
  hasPermission: (permission: string) => boolean;
  /** Refresh user data */
  refreshUser: () => Promise<void>;
  /** Logout user */
  logout: () => Promise<void>;
}

export function useAuthGuard(options: AuthGuardOptions = {}): AuthGuardResult {
  const {
    requiredRoles = [],
    requiredPermissions = [],
    requireAdmin = false,
    redirectTo = '/auth/login',
    showLoading = true
  } = options;

  const router = useRouter();
  const { user: authUser, isAuthenticated, isLoading: authLoading, logout: authLogout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has specific role
  const hasRole = useCallback((role: string): boolean => {
    if (!authUser) return false;

    // Check clearance level for admin roles
    const clearanceLevel = authUser.clearance_level || 'unclassified';

    // Admin role mapping based on clearance level
    if (role === 'admin' || role === 'system_admin') {
      return clearanceLevel === 'top_secret';
    }

    if (role === 'super_admin') {
      return clearanceLevel === 'top_secret';
    }

    // Check department for role-based access
    const department = authUser.department || '';
    if (role === 'trader' && department === 'trading') {
      return true;
    }

    if (role === 'risk' && department === 'risk_management') {
      return true;
    }

    // Check permissions array if available
    const permissions = (authUser as any).permissions || [];
    return permissions.includes(role);
  }, [authUser]);

  // Check if user has specific permission
  const hasPermission = useCallback((permission: string): boolean => {
    if (!authUser) return false;

    // Check clearance level for elevated permissions
    const clearanceLevel = authUser.clearance_level || 'unclassified';

    // Admin users have all permissions
    if (clearanceLevel === 'top_secret') {
      return true;
    }

    // Check need-to-know compartments for compartment-based permissions
    const compartments = authUser.need_to_know_compartments || [];

    // Map permissions to compartments
    const permissionToCompartment: Record<string, string> = {
      'trading': 'trading',
      'portfolio': 'portfolio',
      'orders': 'orders',
      'risk_management': 'risk_management',
      'compliance': 'compliance',
      'audit': 'audit',
      'admin': 'admin',
      'manage_users': 'admin',
      'view_analytics': 'trading',
      'manage_system': 'admin',
      'full_access': 'admin'
    };

    const requiredCompartment = permissionToCompartment[permission];
    if (requiredCompartment && compartments.includes(requiredCompartment)) {
      return true;
    }

    // Check explicit permissions array if available
    const userPermissions = (authUser as any).permissions || [];
    return userPermissions.includes(permission);
  }, [authUser]);

  // Check if user is admin (based on clearance level and department)
  const isAdmin = useCallback((): boolean => {
    if (!authUser) return false;

    // Check clearance level for admin access
    const clearanceLevel = authUser.clearance_level || 'unclassified';
    const department = authUser.department || '';

    // TOP_SECRET clearance or executive department indicates admin
    return clearanceLevel === 'top_secret' || department === 'executive';
  }, [authUser]);

  // Check if user is super admin (highest level admin)
  const isSuperAdmin = useCallback((): boolean => {
    if (!authUser) return false;

    // Only TOP_SECRET clearance with executive department is super admin
    const clearanceLevel = authUser.clearance_level || 'unclassified';
    const department = authUser.department || '';

    return clearanceLevel === 'top_secret' && department === 'executive';
  }, [authUser]);

  // Check if user has required access
  const hasRequiredAccess = useCallback((): boolean => {
    if (!authUser) return false;

    // Check admin requirement
    if (requireAdmin && !isAdmin()) return false;

    // Check required roles
    if (requiredRoles.length > 0) {
      const hasRequiredRole = requiredRoles.some(role => hasRole(role));
      if (!hasRequiredRole) return false;
    }

    // Check required permissions
    if (requiredPermissions.length > 0) {
      const hasRequiredPermission = requiredPermissions.some(permission => hasPermission(permission));
      if (!hasRequiredPermission) return false;
    }

    return true;
  }, [authUser, requireAdmin, requiredRoles, requiredPermissions, isAdmin, hasRole, hasPermission]);

  // Refresh user data (use auth context's refresh)
  const refreshUser = useCallback(async (): Promise<void> => {
    // The auth context handles user refresh
    setIsLoading(false);
  }, []);

  // Logout — authLogout performs hard navigate; do not stack a second push
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authLogout();
    } catch (err) {
      console.error('Logout error:', err);
      // Fallback if hard nav failed mid-flight
      if (typeof window !== 'undefined') {
        window.location.replace(redirectTo || '/auth/login');
      } else {
        router.replace(redirectTo);
      }
    }
  }, [authLogout, router, redirectTo]);

  // Handle access control - only redirect if user is authenticated but doesn't have access
  useEffect(() => {
    if (!authLoading && authUser) {
      const hasAccess = hasRequiredAccess();
      if (!hasAccess) {
        console.warn('Access denied:', {
          user: authUser.username,
          clearance_level: authUser.clearance_level,
          department: authUser.department,
          compartments: authUser.need_to_know_compartments,
          requiredRoles,
          requiredPermissions,
          requireAdmin
        });
        router.push(redirectTo);
      }
    }
  }, [authLoading, authUser, hasRequiredAccess, requiredRoles, requiredPermissions, requireAdmin, router, redirectTo]);

  return {
    user: authUser,
    isAuthenticated,
    hasAccess: hasRequiredAccess(),
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    isLoading: showLoading ? authLoading : false,
    error,
    hasRole,
    hasPermission,
    refreshUser,
    logout
  };
}

/**
 * Admin-specific authentication guard
 * Checks for TOP_SECRET clearance or executive department
 */
export function useAdminAuthGuard(redirectTo: string = '/auth/login'): AuthGuardResult {
  return useAuthGuard({
    requireAdmin: true,
    redirectTo,
    showLoading: true
  });
}

/**
 * Super admin authentication guard
 * Checks for TOP_SECRET clearance AND executive department
 */
export function useSuperAdminAuthGuard(redirectTo: string = '/auth/login'): AuthGuardResult {
  return useAuthGuard({
    requireAdmin: true, // Will be checked by isSuperAdmin() logic
    redirectTo,
    showLoading: true
  });
}
